import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { CmdLoader } from '@commander-services/gui-components';

interface IDetailModalProps {
  id: string;
  width?: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  buttons: IDetailModalButton[];
  loader?: boolean;
}

export interface IDetailModalButton {
  id: string;
  type: 'button' | 'submit' | 'reset';
  title: string;
  className: string;
  submitCallback?: (values: any) => void;
  closeCallback?: (value?: any) => void;
}

const DetailModal: React.FC<IDetailModalProps> = (props) => {
  const { width } = props;
  const modalWidth = width ? { width } : {};

  const handleClose = () => {
    const closeButton: IDetailModalButton | undefined = props.buttons.find(
      (button: IDetailModalButton) => button.closeCallback
    );
    if (closeButton && closeButton.closeCallback) {
      closeButton.closeCallback();
    }
  };

  const getClassName = (key: number) => {
    let className = 'e-button';
    if (props.buttons.length === 1 || key > 0) {
      className = 'e-button e-button--gray';
    }
    return className;
  };

  const handleButtons = async (
    event:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    const activeButton: IDetailModalButton | undefined = props.buttons.find(
      (button: IDetailModalButton) => button.id === event.currentTarget.id
    );

    if (activeButton) {
      if (activeButton.closeCallback) {
        activeButton.closeCallback();
      }
    }
  };

  const handleEscapeKey = React.useCallback(
    (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        const closeButton: IDetailModalButton | undefined = props.buttons.find(
          (button: IDetailModalButton) => button.closeCallback
        );
        if (closeButton && closeButton.closeCallback) {
          closeButton.closeCallback();
        }
      }
    },
    [props.buttons]
  );

  React.useEffect(() => {
    window.addEventListener('keydown', handleEscapeKey);
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [handleEscapeKey]);

  const modalRoot = document.getElementById('modal-root');

  const oldModalContent: JSX.Element = (
    <div
      className="modal modalOpened systemModal"
      id={`simple-modal-${props.id}`}
      tabIndex={-1}
      role="dialog"
      data-keyboard="false"
      data-backdrop="static"
      aria-labelledby="systemModalLabel"
    >
      <div className="modal-dialog" role="document" style={modalWidth}>
        {props.loader && <CmdLoader inContent={true} />}
        <div className="modal-content">
          <div className="modal-header">
            {props.buttons &&
              props.buttons.filter((button: IDetailModalButton) => button.closeCallback)[0] && (
                <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                  <i
                    className="cmd-icon cmd-icon-remove"
                    aria-hidden="true"
                    onClick={handleClose}
                  />
                </button>
              )}
            <h4 className="modal-title" id="simpleModalReadOnlyLabel">
              {props.title}
            </h4>
            {props.subtitle && (
              <span>
                <br />
                {props.subtitle}
              </span>
            )}
          </div>
          <form className="form-horizontal">
            <div className="modal-body">{props.children}</div>
            <div className="modal-footer modal-footer-clear">
              {props.buttons &&
                props.buttons.map((button: IDetailModalButton, key: number) => (
                  <button
                    onClick={handleButtons}
                    data-cy={`button-footer-${button.id}`}
                    key={button.title}
                    type={button.type}
                    id={button.id}
                    className={getClassName(key)}
                  >
                    {button.title}
                  </button>
                ))}
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  if (modalRoot) {
    return ReactDOM.createPortal(oldModalContent, modalRoot);
  }
  console.error('Modal root element is not defined.');
  return <></>;
};

export default DetailModal;
